import {Card, Attributes, AttributesRow, Button, DataStatus} from 'platform/components';
import {Space, VStack, Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {defaultTo, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {GetIssueNoteResponse, useGetWarehouseQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes, workshopRoutes} from '@dms/routes';
import {getCustomerName} from '@dms/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

interface GeneralInformationTabProps extends RequiredTestIdProps {
  issueNote: GetIssueNoteResponse | undefined;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function GeneralInformationTab(props: GeneralInformationTabProps) {
  const formatDateTime = useDateTimeFormatter();

  const directSaleCustomerId = defaultTo(
    '',
    props.issueNote?.originDocTypeInformation.directSale?.customerId
  );
  const serviceOrderCustomerId = defaultTo(
    '',
    props.issueNote?.originDocTypeInformation.serviceOrder?.customerId
  );
  const serviceOrderIssueCustomerId = defaultTo(
    '',
    props.issueNote?.originDocTypeInformation.serviceOrderIssue?.customerId
  );

  const directSaleVehicleId = defaultTo(
    '',
    props.issueNote?.originDocTypeInformation.directSale?.vehicleId
  );
  const serviceOrderVehicleId = defaultTo(
    '',
    props.issueNote?.originDocTypeInformation.serviceOrder?.vehicleId
  );
  const serviceOrderIssueVehicleId = defaultTo(
    '',
    props.issueNote?.originDocTypeInformation.serviceOrderIssue?.vehicleId
  );

  const {data: warehouse, isLoading: isWarehouseLoading} = useGetWarehouseQuery(
    {warehouseId: props.issueNote?.issueNoteInformation?.warehouseId as string},
    {skip: isNilOrEmpty(props.issueNote?.issueNoteInformation?.warehouseId)}
  );

  const {data: directSaleCustomer, isLoading: isDirectSaleCustomerLoading} = useGetCustomerV2Query(
    {customerId: directSaleCustomerId},
    {skip: isNilOrEmpty(directSaleCustomerId)}
  );

  const {data: serviceOrderCustomer, isLoading: isServiceOrderCustomerLoading} =
    useGetCustomerV2Query(
      {customerId: serviceOrderCustomerId},
      {skip: isNilOrEmpty(serviceOrderCustomerId)}
    );

  const {data: serviceOrderIssueCustomer, isLoading: isServiceOrderIssueCustomerLoading} =
    useGetCustomerV2Query(
      {
        customerId: serviceOrderIssueCustomerId,
      },
      {skip: isNilOrEmpty(serviceOrderIssueCustomerId)}
    );

  const {data: directSaleVehicle, isLoading: isDirectSaleVehicleLoading} = useGetVehicleQuery(
    {vehicleId: directSaleVehicleId},
    {skip: isNilOrEmpty(directSaleVehicleId)}
  );

  const {data: serviceOrderVehicle, isLoading: isServiceOrderVehicleLoading} = useGetVehicleQuery(
    {vehicleId: serviceOrderVehicleId},
    {skip: isNilOrEmpty(serviceOrderVehicleId)}
  );

  const {data: serviceOrderIssueVehicle, isLoading: isServiceOrderIssueVehicleLoading} =
    useGetVehicleQuery(
      {vehicleId: serviceOrderIssueVehicleId},
      {skip: isNilOrEmpty(serviceOrderIssueVehicleId)}
    );

  const {data: branchList, isLoading: isBranchListLoading} = useGetBranchListQuery();

  const isLoading =
    isWarehouseLoading ||
    isDirectSaleCustomerLoading ||
    isServiceOrderCustomerLoading ||
    isServiceOrderIssueCustomerLoading ||
    isDirectSaleVehicleLoading ||
    isServiceOrderVehicleLoading ||
    isServiceOrderIssueVehicleLoading ||
    isBranchListLoading;

  const getCurrentDateAndTime = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDateTime('dateTimeShort', parseDate(date));
  };

  const openInNewTab = (
    entity: 'directSales' | 'serviceOrder' | 'serviceOrderIssue' | 'transferOrder',
    id: string | Nullish
  ) => {
    if (isNil(id)) {
      return;
    }

    match(entity)
      .with('directSales', () =>
        window.open(
          composePath(warehouseRoutes.directSalesDetailOverview, {
            params: {id},
            isSearchParamsPreserved: false,
          }),
          '_blank'
        )
      )
      .with('serviceOrder', () =>
        window.open(composePath(workshopRoutes.serviceCaseDetail, {params: {id}}), '_blank')
      )
      .with('serviceOrderIssue', () =>
        window.open(
          composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
            params: {id},
            isSearchParamsPreserved: false,
          }),
          '_blank'
        )
      )
      .with('transferOrder', () =>
        window.open(
          composePath(warehouseRoutes.transferOrderDetailOverview, {
            params: {id},
            isSearchParamsPreserved: false,
          }),
          '_blank'
        )
      )
      .exhaustive();
  };

  const directSaleCustomerName = getCustomerName(directSaleCustomer);
  const serviceOrderCustomerName = getCustomerName(serviceOrderCustomer);
  const serviceOrderIssueCustomerName = getCustomerName(serviceOrderIssueCustomer);

  const issueNoteAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.issuedBy'),
      value: props.issueNote?.issueNoteInformation.issuedBy,
    },
    {
      label: i18n.t('general.labels.receivedBy'),
      value: props.issueNote?.issueNoteInformation.receivedBy,
    },
    {
      label: i18n.t('entity.warehouse.labels.warehouse'),
      value: warehouse?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.dateAndTimeOfCreation'),
      value: getCurrentDateAndTime(props.issueNote?.issueNoteInformation.createdAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.dateAndTimeOfIssue'),
      value: getCurrentDateAndTime(props.issueNote?.issueNoteInformation.issuedAt),
    },
  ];

  const directSalesAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: (
        <Button
          variant="link"
          size="small"
          title={props.issueNote?.originDocTypeInformation.directSale?.number}
          rightIcon="action/launch"
          onClick={() =>
            openInNewTab('directSales', props.issueNote?.originDocTypeInformation.directSale?.id)
          }
          data-testid={suffixTestId('linkToDirectSale', props)}
        />
      ),
    },
    {
      label: i18n.t('general.labels.type'),
      value: props.issueNote?.originDocTypeInformation.directSale?.type,
    },
    {
      label: i18n.t('general.labels.variant'),
      value: props.issueNote?.originDocTypeInformation.directSale?.variant,
    },
    {
      label: i18n.t('entity.warehouse.labels.customer'),
      value: directSaleCustomerName,
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      value: directSaleVehicle?.title,
    },
  ];

  const serviceOrderAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: (
        <Button
          variant="link"
          size="small"
          title={props.issueNote?.originDocTypeInformation.serviceOrder?.number}
          rightIcon="action/launch"
          onClick={() =>
            openInNewTab(
              'serviceOrder',
              props.issueNote?.originDocTypeInformation.serviceOrder?.serviceCaseId
            )
          }
          data-testid={suffixTestId('serviceOrder', props)}
        />
      ),
    },
    {
      label: i18n.t('general.labels.type'),
      value: props.issueNote?.originDocTypeInformation.serviceOrder?.type,
    },
    {
      label: i18n.t('general.labels.variant'),
      value: props.issueNote?.originDocTypeInformation.serviceOrder?.variant,
    },
    {
      label: i18n.t('entity.warehouse.labels.customer'),
      value: serviceOrderCustomerName,
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      value: serviceOrderVehicle?.title,
    },
  ];

  const serviceOrderIssueAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: (
        <Button
          variant="link"
          size="small"
          title={props.issueNote?.originDocTypeInformation.serviceOrderIssue?.number}
          rightIcon="action/launch"
          onClick={() =>
            openInNewTab(
              'serviceOrderIssue',
              props.issueNote?.originDocTypeInformation.serviceOrderIssue?.id
            )
          }
          data-testid={suffixTestId('serviceOrderIssue', props)}
        />
      ),
    },
    {
      label: i18n.t('general.labels.type'),
      value: props.issueNote?.originDocTypeInformation.serviceOrderIssue?.type,
    },
    {
      label: i18n.t('general.labels.variant'),
      value: props.issueNote?.originDocTypeInformation.serviceOrderIssue?.variant,
    },
    {
      label: i18n.t('entity.warehouse.labels.customer'),
      value: serviceOrderIssueCustomerName,
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      value: serviceOrderIssueVehicle?.title,
    },
  ];

  const sourceBranchName = branchList?.branchListItems?.find(
    (branch) =>
      branch.id === props.issueNote?.originDocTypeInformation.transferOrder?.sourceBranchId
  )?.marketingName;

  const destinationBranchName = branchList?.branchListItems?.find(
    (branch) =>
      branch.id === props.issueNote?.originDocTypeInformation.transferOrder?.destinationBranchId
  )?.marketingName;

  const transferOrderAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: (
        <Button
          variant="link"
          size="small"
          title={props.issueNote?.originDocTypeInformation.transferOrder?.number}
          rightIcon="action/launch"
          onClick={() =>
            openInNewTab(
              'transferOrder',
              props.issueNote?.originDocTypeInformation.transferOrder?.id
            )
          }
          data-testid={suffixTestId('transferOrder', props)}
        />
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.transferOrderType'),
      value: match(props.issueNote?.originDocTypeInformation.transferOrder?.destinationType)
        .with('warehouse', () => i18n.t('entity.warehouse.labels.warehouse'))
        .with('authorization', () => i18n.t('entity.warehouse.labels.authorization'))
        .with('branch', () => i18n.t('entity.warehouse.labels.branch'))
        .with('external', () => i18n.t('entity.warehouse.labels.external'))
        .otherwise(() => EMPTY_PLACEHOLDER),
    },
    {
      label: i18n.t('entity.warehouse.labels.transferOrderVariant'),
      value: props.issueNote?.originDocTypeInformation.transferOrder?.variant,
    },
    {
      label: i18n.t('entity.warehouse.labels.sourceBranch'),
      value: sourceBranchName,
    },
    {
      label: i18n.t('entity.warehouse.labels.destinationBranch'),
      value: destinationBranchName,
    },
  ];

  return (
    <DataStatus isLoading={isLoading} minHeight={60}>
      <Card title={i18n.t('entity.issueNotes.labels.issueNoteInformation')}>
        <Attributes
          rows={issueNoteAttributes}
          size="quarter"
          data-testid={suffixTestId('issueNoteAttributes', props)}
        />

        <Space vertical={4} />

        <VStack spacing={4}>
          <Show when={isNotNil(props.issueNote?.originDocTypeInformation.directSale)}>
            <Card title={i18n.t('entity.issueNote.labels.directSales')} variant="inlineGrey">
              <Attributes
                rows={directSalesAttributes}
                size="quarter"
                data-testid={suffixTestId('directSalesAttributes', props)}
              />
            </Card>
          </Show>
          <Show when={isNotNil(props.issueNote?.originDocTypeInformation.serviceOrder)}>
            <Card title={i18n.t('entity.issueNote.labels.serviceOrder')} variant="inlineGrey">
              <Attributes
                rows={serviceOrderAttributes}
                size="quarter"
                data-testid={suffixTestId('serviceOrderAttributes', props)}
              />
            </Card>
          </Show>
          <Show when={isNotNil(props.issueNote?.originDocTypeInformation.serviceOrderIssue)}>
            <Card title={i18n.t('entity.issueNote.labels.serviceOrderIssue')} variant="inlineGrey">
              <Attributes
                rows={serviceOrderIssueAttributes}
                size="quarter"
                data-testid={suffixTestId('serviceOrderIssueAttributes', props)}
              />
            </Card>
          </Show>
          <Show when={isNotNil(props.issueNote?.originDocTypeInformation.transferOrder)}>
            <Card title={i18n.t('entity.warehouse.labels.transferOrder')} variant="inlineGrey">
              <Attributes
                rows={transferOrderAttributes}
                size="quarter"
                data-testid={suffixTestId('transferOrderAttributes', props)}
              />
            </Card>
          </Show>
        </VStack>
      </Card>
    </DataStatus>
  );
}
